import type { ReactNode } from "react"
import ValueWithDelta from "~/ui-rtk/components/ui/common/ValueWithDelta/ValueWithDelta"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/**
 * Spreadsheet-styled primitives for the Ad Blast Reports page: gridlined
 * cells, section bands and KPI tiles in the app's dark system palette (the
 * same surfaces the Jobs / Scheduled Jobs tables use), laid out like the
 * Google Sheet the agency fills by hand.
 */

export type Tone = "default" | "bad" | "good" | "warn" | "muted"

const TONE_CLASSES: Record<Tone, string> = {
  default: "bg-basic-dark-blue",
  bad: "bg-rose-500/10",
  good: "bg-emerald-500/10",
  warn: "bg-amber-500/10",
  muted: "bg-white/5 text-system-gray-400",
}

export const CELL =
  "border-b border-r border-system-blue-600/40 px-2 py-1 align-middle text-[13px] text-system-gray-100"

/** Scroll container: gridline top/left edges + both-axis scrolling. */
export const GRID_WRAPPER =
  "overflow-auto border-l border-t border-system-blue-600/40 bg-basic-dark-blue"

/** Header cells stay pinned while the body scrolls inside the wrapper. */
const STICKY_HEADER = "sticky top-0 z-10"

/** Blue band with a bold title — the sheet's section header row. */
export function SectionBand({
  title,
  children,
}: {
  title: string
  children?: ReactNode
}) {
  return (
    <div className="mt-4 flex items-center justify-between border border-system-blue-600/40 bg-system-blue-700 px-2 py-1.5 text-[15px] font-bold text-white first:mt-0">
      <span>{title}</span>
      {children}
    </div>
  )
}

export interface SheetColumn<Row> {
  header: string
  render: (row: Row) => ReactNode
  align?: "left" | "right" | "center"
  className?: string
}

/**
 * A gridlined table. `tone` colors a whole row (red = short of goal, green =
 * over goal…), `emptyLabel` renders one muted row when there is no data so the
 * sheet never shows a bare header. Scrolls on both axes inside its wrapper:
 * `maxHeight` (a Tailwind max-h class, default 60vh) bounds the body while
 * the header row stays pinned.
 */
export function SheetTable<Row>({
  columns,
  rows,
  rowKey,
  tone,
  emptyLabel = "None",
  dense,
  maxHeight = "max-h-[60vh]",
}: {
  columns: SheetColumn<Row>[]
  rows: Row[]
  rowKey: (row: Row) => string
  tone?: (row: Row) => Tone
  emptyLabel?: string
  dense?: boolean
  /** Tailwind max-height class for the scroll container. */
  maxHeight?: string
}) {
  return (
    <div className={cn(GRID_WRAPPER, maxHeight)}>
      <table className="w-max min-w-full border-separate border-spacing-0">
        <thead>
          <tr>
            {columns.map(column => (
              <th
                key={column.header}
                className={cn(
                  CELL,
                  STICKY_HEADER,
                  "bg-system-blue-800 text-center text-[11px] font-semibold uppercase tracking-wide text-system-gray-400",
                  dense && "py-0.5 text-[12px]",
                  column.className,
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={columns.length}
                className={cn(CELL, "bg-basic-dark-blue text-system-gray-500")}
              >
                {emptyLabel}
              </td>
            </tr>
          )}
          {rows.map(row => {
            const rowTone = tone?.(row) ?? "default"
            return (
              <tr
                key={rowKey(row)}
                className={cn(TONE_CLASSES[rowTone], "hover:bg-white/5")}
              >
                {columns.map(column => (
                  <td
                    key={column.header}
                    className={cn(
                      CELL,
                      column.align === "right" && "text-right tabular-nums",
                      column.align === "center" && "text-center",
                      dense && "py-0.5 text-[12px]",
                      column.className,
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Big-number tile row — the sheet's "5-second snapshot". */
export function KpiRow({
  items,
}: {
  items: Array<{ label: string; value: ReactNode }>
}) {
  return (
    <div className={GRID_WRAPPER}>
      <table className="w-max min-w-full border-separate border-spacing-0">
        <thead>
          <tr>
            {items.map(item => (
              <th
                key={item.label}
                className={cn(
                  CELL,
                  "bg-system-blue-800 text-center text-[11px] font-semibold uppercase tracking-wide text-system-gray-400",
                )}
              >
                {item.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {items.map(item => (
              <td
                key={item.label}
                className={cn(
                  CELL,
                  "bg-basic-dark-blue py-2 text-center text-[22px] font-bold tabular-nums text-white",
                )}
              >
                {item.value}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/** Two-column label / value strip (the sheet's "Last refreshed … As of date" row). */
export function MetaStrip({
  items,
}: {
  items: Array<{ label: string; value: ReactNode }>
}) {
  return (
    <div className="flex flex-wrap border border-system-blue-600/40 bg-system-blue-800/60 text-[12px] text-system-gray-400">
      {items.map(item => (
        <div
          key={item.label}
          className="flex items-center gap-2 border-r border-system-blue-600/40 px-2 py-1 last:border-r-0"
        >
          <span>{item.label}</span>
          <span className="font-medium text-system-gray-100">{item.value}</span>
        </div>
      ))}
    </div>
  )
}

/** Free-text sheet lines — the "Definitions" block. */
export function DefinitionList({
  items,
}: {
  items: Array<{ term: string; text: string }>
}) {
  return (
    <div className="border border-t-0 border-system-blue-600/40 bg-basic-dark-blue text-[12px] text-system-gray-400">
      {items.map(item => (
        <div
          key={item.term}
          className="flex gap-3 border-b border-white/5 px-2 py-1 last:border-b-0"
        >
          <span className="w-24 shrink-0 font-medium text-system-gray-100">
            {item.term}
          </span>
          <span>{item.text}</span>
        </div>
      ))}
    </div>
  )
}

function statusTone(status: string): string {
  if (/^live/i.test(status)) return "bg-emerald-500/20 text-emerald-200"
  if (status === "Failed") return "bg-rose-500/20 text-rose-200"
  if (/paused/i.test(status)) return "bg-amber-500/20 text-amber-200"
  return "bg-white/10 text-system-gray-200"
}

export function StatusPill({ status }: { status: string }) {
  const tone = statusTone(status)
  return (
    <span
      className={cn(
        "inline-block rounded px-1.5 py-0.5 text-[11px] font-medium",
        tone,
      )}
    >
      {status}
    </span>
  )
}

/**
 * A number with its comparison-period reading underneath, the way the
 * project's compare-mode tables do it: current value, then a smaller line with
 * the trend percentage and "prev: N". Renders the bare value when there is no
 * comparison period.
 */
export function CompareValue({
  value,
  previous,
  label,
  align = "end",
}: {
  value: number
  previous?: number
  /** Overrides the top line (e.g. "3 / 2" for launched / live pairs). */
  label?: ReactNode
  /** Right-aligned in numeric table cells; centered inside KPI tiles. */
  align?: "end" | "center"
}) {
  if (previous === undefined) return <>{label ?? value}</>
  return (
    <div
      className={cn(
        "flex flex-col leading-tight",
        align === "center" ? "items-center" : "items-end",
      )}
    >
      <span>{label ?? value}</span>
      <span className="flex items-center gap-1 text-[11px] font-normal text-system-gray-400">
        <ValueWithDelta
          actualValue={value}
          comparedValue={previous}
          hideActualValue
          size={12}
        />
        prev: {previous}
      </span>
    </div>
  )
}

/**
 * Who ran the action. Audit lines carry `"Name <email>"` for a user, or a
 * bare id / "system" for automated runs; show the name with the email muted.
 */
export function Person({ value }: { value: string }) {
  const match = value.match(/^(.*?)\s*<([^>]+)>$/)
  if (!match) return <span className="whitespace-nowrap">{value || "—"}</span>
  const [, name, email] = match
  return (
    <span className="flex flex-col leading-tight">
      <span className="whitespace-nowrap">{name || email}</span>
      <span className="text-[11px] text-system-gray-500">{email}</span>
    </span>
  )
}

export function ExternalLink({ href, label }: { href: string; label: string }) {
  if (!href) return <span className="text-system-gray-500">—</span>
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="text-sky-300 underline decoration-sky-300/40 underline-offset-2 hover:decoration-sky-300"
    >
      {label}
    </a>
  )
}

/** The ad's name, linked to it in Ads Manager whenever the line has a preview URL. */
export function AdNameRef({
  name,
  previewUrl,
}: {
  name: string
  previewUrl: string
}) {
  return previewUrl ? (
    <ExternalLink href={previewUrl} label={name} />
  ) : (
    <>{name}</>
  )
}

/** Blast source kinds as the sheet's "Type" column reads them. */
const TYPE_LABELS: Record<string, string> = {
  post: "Social post",
  ad: "Existing ad",
  creative: "Meta creative",
  adCreative: "AdForge creative",
}

export const typeLabel = (kind: string): string => TYPE_LABELS[kind] ?? kind

/** Facebook page posts (`pageId_postId`) link straight to the post; IG media ids have no public URL. */
export function PostRef({ postId }: { postId: string }) {
  if (!postId) return <span className="text-system-gray-500">—</span>
  if (/^\d+_\d+$/.test(postId)) {
    return (
      <ExternalLink
        href={`https://www.facebook.com/${postId}`}
        label={postId}
      />
    )
  }
  return <span className="tabular-nums">{postId}</span>
}
